import * as THREE from 'three';

const COLOR_HOT = 0xeaffd0;     // near-white inner streak
const COLOR_CORE = 0xb7ff6a;    // saturated mid-green plasma
const COLOR_HALO = 0x6fd936;    // wide outer atmospheric scatter
const COLOR_IMPACT = 0xfaffe6;  // hot-white impact spot
const COLOR_STAR = 0xc9ff7a;    // tinted star-burst at impact

/**
 * Continuous mining laser shared by NPC miners, the ship right-click
 * beam, and the FPS hand cutter. The beam is rendered as three axial
 * billboard planes (hot streak, core, halo) — they always face the
 * camera while keeping the beam axis fixed, which avoids the facet
 * artifacts a stiff cylinder shows at close (FPS) range. A bright
 * impact sprite plus a slow-rotating star burst sells the contact
 * point, and a small origin spot keeps the muzzle reading hot.
 *
 * The camera reference is required because the billboard orientation
 * has to be recomputed every time the endpoints change.
 */
export class MiningBeam {
  readonly object3D: THREE.Group;

  private readonly hot: THREE.Mesh;
  private readonly core: THREE.Mesh;
  private readonly halo: THREE.Mesh;
  private readonly hotMat: THREE.MeshBasicMaterial;
  private readonly coreMat: THREE.MeshBasicMaterial;
  private readonly haloMat: THREE.MeshBasicMaterial;

  private readonly impactSprite: THREE.Sprite;
  private readonly impactStar: THREE.Sprite;
  private readonly originSprite: THREE.Sprite;
  private readonly impactSpriteMat: THREE.SpriteMaterial;
  private readonly impactStarMat: THREE.SpriteMaterial;
  private readonly originSpriteMat: THREE.SpriteMaterial;

  private readonly gradientTex: THREE.DataTexture;
  private readonly dotTex: THREE.DataTexture;
  private readonly starTex: THREE.DataTexture;

  private readonly cameraPos = new THREE.Vector3();
  private readonly tmpDir = new THREE.Vector3();
  private readonly tmpMid = new THREE.Vector3();
  private readonly tmpRight = new THREE.Vector3();
  private readonly tmpForward = new THREE.Vector3();
  private readonly tmpToCam = new THREE.Vector3();
  private readonly tmpUp = new THREE.Vector3();
  private readonly tmpMat = new THREE.Matrix4();

  private active = false;
  private pulse = 0;
  private impactPulse = 0;

  constructor(private readonly camera: THREE.Camera) {
    this.object3D = new THREE.Group();
    this.object3D.name = 'mining-beam';
    this.object3D.visible = false;

    this.gradientTex = createBeamGradientTexture();
    this.dotTex = createSoftDotTexture();
    this.starTex = createStarBurstTexture();

    // ── Hot inner streak — narrow, almost-white. The "blade" of the beam.
    this.hotMat = new THREE.MeshBasicMaterial({
      map: this.gradientTex,
      color: COLOR_HOT,
      transparent: true,
      opacity: 1.0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false
    });
    this.hot = new THREE.Mesh(new THREE.PlaneGeometry(0.22, 1, 1, 1), this.hotMat);
    this.hot.renderOrder = 4;

    // ── Saturated core — the green plasma read.
    this.coreMat = new THREE.MeshBasicMaterial({
      map: this.gradientTex,
      color: COLOR_CORE,
      transparent: true,
      opacity: 0.85,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false
    });
    this.core = new THREE.Mesh(new THREE.PlaneGeometry(0.6, 1, 1, 1), this.coreMat);
    this.core.renderOrder = 3;

    // ── Outer halo — soft luminous bloom, low opacity.
    this.haloMat = new THREE.MeshBasicMaterial({
      map: this.gradientTex,
      color: COLOR_HALO,
      transparent: true,
      opacity: 0.32,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false
    });
    this.halo = new THREE.Mesh(new THREE.PlaneGeometry(1.6, 1, 1, 1), this.haloMat);
    this.halo.renderOrder = 2;

    this.object3D.add(this.halo, this.core, this.hot);

    // ── Impact hot spot — bright dot at the contact point.
    this.impactSpriteMat = new THREE.SpriteMaterial({
      map: this.dotTex,
      color: COLOR_IMPACT,
      transparent: true,
      opacity: 1.0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false
    });
    this.impactSprite = new THREE.Sprite(this.impactSpriteMat);
    this.impactSprite.renderOrder = 5;
    this.impactSprite.scale.set(0.55, 0.55, 1);
    this.object3D.add(this.impactSprite);

    // ── 4-point star burst over the impact — slowly rotates so the
    // contact point feels alive rather than a static decal.
    this.impactStarMat = new THREE.SpriteMaterial({
      map: this.starTex,
      color: COLOR_STAR,
      transparent: true,
      opacity: 0.9,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false
    });
    this.impactStar = new THREE.Sprite(this.impactStarMat);
    this.impactStar.renderOrder = 5;
    this.impactStar.scale.set(2.2, 2.2, 1);
    this.object3D.add(this.impactStar);

    // ── Muzzle glow at the origin. Sells the "leaving the emitter"
    // beat in FPS view; barely visible at ship distances.
    this.originSpriteMat = new THREE.SpriteMaterial({
      map: this.dotTex,
      color: COLOR_HOT,
      transparent: true,
      opacity: 0.85,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false
    });
    this.originSprite = new THREE.Sprite(this.originSpriteMat);
    this.originSprite.renderOrder = 5;
    this.originSprite.scale.set(0.32, 0.32, 1);
    this.object3D.add(this.originSprite);
  }

  setEndpoints(from: THREE.Vector3, to: THREE.Vector3): void {
    this.active = true;
    this.object3D.visible = true;

    this.tmpDir.copy(to).sub(from);
    const length = this.tmpDir.length();
    if (length < 1e-4) {
      this.object3D.visible = false;
      return;
    }
    this.tmpDir.multiplyScalar(1 / length);

    this.tmpMid.copy(from).addScaledVector(this.tmpDir, length * 0.5);
    this.camera.getWorldPosition(this.cameraPos);
    this.tmpToCam.copy(this.cameraPos).sub(this.tmpMid);

    // Build an axial-billboard basis: localY runs along the beam, localZ
    // (the plane's normal) is the in-plane axis closest to the camera.
    // Cross of beamDir × camVec gives the perpendicular "right" axis;
    // when the camera is on the beam axis we fall back to any stable
    // perpendicular so the beam doesn't pop.
    this.tmpRight.crossVectors(this.tmpDir, this.tmpToCam);
    if (this.tmpRight.lengthSq() < 1e-6) {
      this.tmpUp.set(Math.abs(this.tmpDir.y) > 0.9 ? 1 : 0, Math.abs(this.tmpDir.y) > 0.9 ? 0 : 1, 0);
      this.tmpRight.crossVectors(this.tmpDir, this.tmpUp).normalize();
    } else {
      this.tmpRight.normalize();
    }
    this.tmpForward.crossVectors(this.tmpRight, this.tmpDir).normalize();
    this.tmpMat.makeBasis(this.tmpRight, this.tmpDir, this.tmpForward);

    this.hot.position.copy(this.tmpMid);
    this.core.position.copy(this.tmpMid);
    this.halo.position.copy(this.tmpMid);
    this.hot.quaternion.setFromRotationMatrix(this.tmpMat);
    this.core.quaternion.setFromRotationMatrix(this.tmpMat);
    this.halo.quaternion.setFromRotationMatrix(this.tmpMat);

    // Width sits on the plane geometry; only length needs scaling.
    this.hot.scale.set(1, length, 1);
    this.core.scale.set(1, length, 1);
    this.halo.scale.set(1, length, 1);

    this.impactSprite.position.copy(to);
    this.impactStar.position.copy(to);
    this.originSprite.position.copy(from);
  }

  tick(dt: number): void {
    if (!this.active) return;
    this.pulse += dt * 9;
    this.impactPulse += dt * 16;

    // Two phase-shifted sines stack a fast flicker on a slower wobble so
    // the beam feels alive without a visible repeating beat.
    const fast = 0.85 + Math.sin(this.pulse) * 0.15;
    const slow = 0.85 + Math.sin(this.pulse * 0.45) * 0.15;
    const jitter = 0.92 + Math.sin(this.pulse * 3.7) * 0.08;

    this.hotMat.opacity = (0.92 + (1 - fast) * 0.08) * jitter;
    this.coreMat.opacity = 0.72 * fast + 0.18;
    this.haloMat.opacity = 0.28 * slow + 0.10;

    // Impact pulsation — two harmonics + a slow rotation on the star.
    const impactScale = 0.55 * (0.85 + Math.sin(this.impactPulse) * 0.12 + Math.sin(this.impactPulse * 2.7) * 0.06);
    this.impactSprite.scale.set(impactScale, impactScale, 1);
    this.impactSpriteMat.opacity = 0.9 + Math.sin(this.impactPulse * 1.3) * 0.1;

    const starScale = 2.2 * (0.85 + Math.sin(this.impactPulse * 1.6) * 0.15);
    this.impactStar.scale.set(starScale, starScale, 1);
    this.impactStarMat.rotation = this.impactPulse * 0.35;
    this.impactStarMat.opacity = 0.55 + Math.sin(this.impactPulse * 0.8) * 0.25;

    this.originSpriteMat.opacity = 0.7 + Math.sin(this.pulse * 1.4) * 0.2;
  }

  hide(): void {
    this.active = false;
    this.object3D.visible = false;
  }

  dispose(): void {
    this.hot.geometry.dispose();
    this.core.geometry.dispose();
    this.halo.geometry.dispose();
    this.hotMat.dispose();
    this.coreMat.dispose();
    this.haloMat.dispose();
    this.impactSpriteMat.dispose();
    this.impactStarMat.dispose();
    this.originSpriteMat.dispose();
    this.gradientTex.dispose();
    this.dotTex.dispose();
    this.starTex.dispose();
  }
}

/** Vertical-stripe gradient: bright at horizontal centre, fading to 0
 *  at the edges. Used for all three beam layers — the visible width is
 *  controlled by each plane's geometry, so one texture covers thin
 *  hot-streak through wide halo. */
function createBeamGradientTexture(): THREE.DataTexture {
  const width = 64;
  const height = 4;
  const data = new Uint8Array(width * height * 4);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const t = (x / (width - 1)) * 2 - 1;
      // Soft bell curve, clipped to fully transparent at the silhouette.
      const alpha = Math.max(0, Math.pow(1 - Math.abs(t), 1.8));
      const i = (y * width + x) * 4;
      data[i] = 255;
      data[i + 1] = 255;
      data[i + 2] = 255;
      data[i + 3] = Math.round(alpha * 255);
    }
  }
  const tex = new THREE.DataTexture(data, width, height, THREE.RGBAFormat, THREE.UnsignedByteType);
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.needsUpdate = true;
  return tex;
}

/** Radial dot with sharp centre and soft falloff — used for the impact
 *  hot spot and the muzzle origin glow. */
function createSoftDotTexture(): THREE.DataTexture {
  const size = 64;
  const data = new Uint8Array(size * size * 4);
  const c = (size - 1) / 2;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = (x - c) / c;
      const dy = (y - c) / c;
      const r = Math.min(1, Math.sqrt(dx * dx + dy * dy));
      const alpha = Math.pow(Math.max(0, 1 - r), 2.2);
      const i = (y * size + x) * 4;
      data[i] = 255;
      data[i + 1] = 255;
      data[i + 2] = 255;
      data[i + 3] = Math.round(alpha * 255);
    }
  }
  const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat, THREE.UnsignedByteType);
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.needsUpdate = true;
  return tex;
}

/** Lens-flare style 4-point star: two crossed bars + a soft round core,
 *  with smooth radial falloff at the tips. The slow rotation we apply to
 *  this sprite gives the impact a live "scintillation" beat. */
function createStarBurstTexture(): THREE.DataTexture {
  const size = 64;
  const data = new Uint8Array(size * size * 4);
  const c = (size - 1) / 2;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = (x - c) / c;
      const dy = (y - c) / c;
      const dist = Math.min(1, Math.sqrt(dx * dx + dy * dy));
      const radial = Math.max(0, 1 - dist);
      const horizontalBar = Math.exp(-dy * dy * 80) * radial;
      const verticalBar = Math.exp(-dx * dx * 80) * radial;
      const center = Math.exp(-(dist * dist) * 9);
      const alpha = Math.min(1, horizontalBar + verticalBar + center * 0.7);
      const i = (y * size + x) * 4;
      data[i] = 255;
      data[i + 1] = 255;
      data[i + 2] = 255;
      data[i + 3] = Math.round(alpha * 255);
    }
  }
  const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat, THREE.UnsignedByteType);
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.needsUpdate = true;
  return tex;
}
